import { isAxiosError } from 'axios'

interface ApiErrorBody {
  code: string
  message: string
}

// Kända backend-meddelanden (src/error i backend) översatta till svenska, så en specifik
// orsak (t.ex. skyddet för sista superadmin) inte döljs bakom ett generiskt felkodsmeddelande.
const MESSAGE_TRANSLATIONS: Record<string, string> = {
  'Invalid credentials': 'Fel e-post eller lösenord.',
  'Email already in use': 'E-postadressen används redan.',
  'Cannot deactivate your own account': 'Du kan inte inaktivera ditt eget konto.',
  'Insufficient permissions': 'Du saknar behörighet för detta.',
  'User not found': 'Användaren hittades inte.',
  'Cannot remove the last active superadmin': 'Det går inte – det måste alltid finnas minst en aktiv superadmin.',
  'Role cannot be assigned': 'Den rollen kan inte tilldelas.',
  'Schemat är inte ett giltigt cron-uttryck med fem fält': 'Schemat gick inte att spara. Välj tiderna på nytt.',
  'Schemat kör aldrig': 'Med de här valen körs jobbet aldrig. Välj andra dagar eller tider.',
  'Schemat får inte köra oftare än var 15:e minut': 'Jobbet kan köras högst var 15:e minut. Välj färre tider.',
  'Abonnemanget tar inte emot fler leveranser': 'Abonnemanget tar inte emot fler leveranser.',
  'Kunden måste verifieras innan en leverans kan skapas': 'Kunden måste verifieras innan en leverans kan skapas.',
  'Leveranser är avstängda för kunden': 'Leveranser är avstängda för kunden. Aktivera leveranser först.',
  'Leveranserna har ändrats sedan sidan laddades': 'Leveranserna har ändrats sedan sidan laddades. Ladda om sidan och försök igen.',
  'Leveransen har lämnats in hos PostNord och kan inte tas bort': 'Leveransen har lämnats in hos PostNord och kan inte tas bort.',
  'Leveransen är redan avslutad och kan inte tas bort': 'Leveransen är redan avslutad och kan inte tas bort.',
  'Bara en leverans som är på väg kan skrivas av': 'Bara en leverans som är på väg kan skrivas av. Ladda om sidan.',
  'Abonnemanget kan inte avslutas i nuvarande status': 'Abonnemanget kan inte avslutas i nuvarande status. Ladda om sidan.',
  'Fakturan är redan makulerad': 'Fakturan är redan makulerad.',
  'Fakturan kunde inte makuleras hos Capway': 'Fakturan kunde inte makuleras hos Capway just nu. Försök igen om en stund.',
  'Prenumerationen kan inte ångras i nuvarande status': 'Abonnemanget kan inte ångras i nuvarande status. Ladda om sidan.',
  'Prenumerationen kan inte sägas upp i nuvarande status': 'Abonnemanget kan inte sägas upp i nuvarande status. Ladda om sidan.',
  'Den sista leveransen kan inte ligga före en leverans som redan skapats':
    'En senare leverans har redan skapats. Ladda om sidan och välj igen.',
  'PostNord svarar inte, så leveransen kunde inte kontrolleras':
    'PostNord svarar inte just nu, så det går inte att kontrollera om paketet har lämnats in. Försök igen om en stund.',
  'Kunden finns inte i arbetslistan': 'Kunden finns inte i arbetslistan. Läs in arbetslistan igen.',
  'Kunden är redan inlagd; ångra inläggningen först': 'Kunden är redan inlagd. Ångra inläggningen först.',
  'Inläggningen finns inte': 'Inläggningen finns inte.',
  'Inläggningen är redan ångrad': 'Inläggningen är redan ångrad.',
  'Capway nekar inloggning; ingen ny inloggning görs': 'Capway nekar inloggning. Ingen ny inloggning görs; säg till Ahmad.',
  'Capway svarar inte': 'Capway svarar inte just nu. Försök igen om en stund.'
}

// Meddelanden som bär sin egen förklaring, som Historik-sidans "Ingenting skrivet: …" med problemen, visas som de är.
const VERBATIM_PREFIXES = ['Ingenting skrivet:', 'Arbetslistan:', 'Kunderna ']

// Svenska standardmeddelanden per felkod, för sådant som inte finns i översättningarna ovan
// (t.ex. ett rått valideringsmeddelande på engelska för bad_request).
const CODE_FALLBACKS: Record<string, string> = {
  bad_request: 'Uppgifterna verkar inte stämma. Kontrollera fälten och försök igen.',
  unauthorized: 'Du är inte inloggad. Logga in igen.',
  forbidden: 'Du saknar behörighet för detta.',
  not_found: 'Hittades inte.',
  conflict: 'Det gick inte att spara.',
  internal_error: 'Något gick fel. Försök igen om en stund.'
}

function getApiErrorBody(error: unknown): ApiErrorBody | null {
  if (!isAxiosError(error)) return null

  const data = error.response?.data

  if (!data || typeof data !== 'object' || typeof (data as Record<string, unknown>).code !== 'string') return null

  return data as ApiErrorBody
}

/** Läser backendens {code, message}-felformat och ger ett begripligt, svenskt meddelande. */
export function getApiErrorMessage(error: unknown, fallback = 'Något gick fel. Försök igen.'): string {
  const body = getApiErrorBody(error)

  if (!body) return fallback
  if (VERBATIM_PREFIXES.some((prefix) => body.message.startsWith(prefix))) return body.message

  return MESSAGE_TRANSLATIONS[body.message] ?? CODE_FALLBACKS[body.code] ?? fallback
}
